import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  PhoneIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

// ---------------------------------------------------------------------------
// Shared WhatsApp config surface (Card A = WABA, Card B = automatic messages).
//
// Extracted from the old Settings.js inline block so the page lives under the
// WhatsApp section (Configuración tab) and Settings.js no longer carries a
// divergent copy. Wired to the single GET/PUT /tenants/me/whatsapp-config
// endpoint — one save -> one toast.
//
// Defensive-robust lens: every server value is coerced before it touches
// state, the automsg rules are normalized via normalizeAutomsgRule, and the
// load/save paths are wrapped so a malformed payload never throws into render.
// ---------------------------------------------------------------------------

// Sales (Ventas) outcome buckets.
const SALES_RULE_KEYS = [
  'whatsapp_automsg_interested',
  'whatsapp_automsg_not_interested',
  'whatsapp_automsg_no_answer',
];
// Postventa prospección buckets (GM BDC Posventa 2026), PRIORITY-segmented.
// Fired proactively by the cadence engine (send-due), classified per vehicle.
const POSTVENTA_RULE_KEYS = [
  'whatsapp_automsg_postventa_1a',
  'whatsapp_automsg_postventa_1b',
  'whatsapp_automsg_postventa_2a',
  'whatsapp_automsg_postventa_2b',
  'whatsapp_automsg_postventa_franja',
];
const ALL_RULE_KEYS = [...SALES_RULE_KEYS, ...POSTVENTA_RULE_KEYS];

// Postventa POST-CALL / per-outcome rules (issue #1308 + R4-D3) and the
// day-of-appointment reminder (Roger, Oct 9). These are TEMPLATE-driven: the
// send path needs an approved Twilio Content SID; `message` is the human note /
// fallback body shown to the admin. Until the Oct 10 frontend audit these keys
// were sent LIVE but invisible/non-editable here (fixed bucket list above), so
// Hugo/Fer could only change them via CLI/DB.
//
// Contract (mirrors the backend PUT, which only SETs a rule when the key is
// PRESENT in the body): a key is kept in local state ONLY when the server
// returned it or the admin edited it, and ONLY those keys are sent on save.
// Keys absent from GET are never sent as empty strings, so raw-DDB/operator
// values and server-side defaults are never clobbered by a plain "Guardar".
const POSTVENTA_POST_CALL_RULE_KEYS = [
  'whatsapp_automsg_postventa_post_call',
  'whatsapp_automsg_postventa_confirmacion',
  'whatsapp_automsg_postventa_seguimiento',
  'whatsapp_automsg_postventa_neutral',
  'whatsapp_automsg_postventa_cierre',
  'whatsapp_automsg_postventa_no_contesto',
  // oct19 (José / Grupo Cruces contact cadence): last no-contestó WA of the
  // contact week carries the opt-out menu (1 Sí / 2 Por el momento no / 3 Baja).
  'whatsapp_automsg_postventa_no_contesto_optout',
];
const DAY_REMINDER_KEY = 'whatsapp_appointment_day_reminder';

// Postventa INFORMATIVO (Track B) rotating bucket. Distinct shape from the flat
// rules above: it carries two parallel maps keyed by 1-based string index
// ("1".."10") — template_sids (idx -> approved Twilio content SID) and messages
// (idx -> default body). Handled by its own normalizer + render branch (NOT via
// normalizeAutomsgRule/ALL_RULE_KEYS) because the shape differs.
const INFORMATIVO_KEY = 'whatsapp_automsg_postventa_informativo';
const INFORMATIVO_COUNT = 10;
const INFORMATIVO_IDXS = Array.from({ length: INFORMATIVO_COUNT }, (_, i) => String(i + 1));
// idx -> calendar-month hint (mirrors GM_INFORMATIVO_CALENDAR bias, advisory).
const INFORMATIVO_IDX_HINT = {
  '1': '6 meses', '2': '9 meses', '3': '3 meses', '4': '12 meses', '5': 'rotación',
  '6': 'rotación', '7': 'rotación', '8': 'rotación', '9': 'rotación', '10': 'rotación',
};

// Color theme per outcome (green / red / amber / teal for the proactive one).
const RULE_THEME = {
  whatsapp_automsg_interested: { accent: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', ring: 'border-emerald-200 dark:border-emerald-500/30' },
  whatsapp_automsg_not_interested: { accent: 'text-red-600 dark:text-red-400', dot: 'bg-red-500', ring: 'border-red-200 dark:border-red-500/30' },
  whatsapp_automsg_no_answer: { accent: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-500', ring: 'border-amber-200 dark:border-amber-500/30' },
  whatsapp_automsg_postventa_1a: { accent: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', ring: 'border-emerald-200 dark:border-emerald-500/30' },
  whatsapp_automsg_postventa_1b: { accent: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', ring: 'border-emerald-200 dark:border-emerald-500/30' },
  whatsapp_automsg_postventa_2a: { accent: 'text-teal-600 dark:text-teal-400', dot: 'bg-teal-500', ring: 'border-teal-200 dark:border-teal-500/30' },
  whatsapp_automsg_postventa_2b: { accent: 'text-teal-600 dark:text-teal-400', dot: 'bg-teal-500', ring: 'border-teal-200 dark:border-teal-500/30' },
  whatsapp_automsg_postventa_franja: { accent: 'text-indigo-600 dark:text-indigo-400', dot: 'bg-indigo-500', ring: 'border-indigo-200 dark:border-indigo-500/30' },
  whatsapp_automsg_postventa_informativo: { accent: 'text-sky-600 dark:text-sky-400', dot: 'bg-sky-500', ring: 'border-sky-200 dark:border-sky-500/30' },
  whatsapp_automsg_postventa_post_call: { accent: 'text-violet-600 dark:text-violet-400', dot: 'bg-violet-500', ring: 'border-violet-200 dark:border-violet-500/30' },
  whatsapp_automsg_postventa_confirmacion: { accent: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', ring: 'border-emerald-200 dark:border-emerald-500/30' },
  whatsapp_automsg_postventa_seguimiento: { accent: 'text-teal-600 dark:text-teal-400', dot: 'bg-teal-500', ring: 'border-teal-200 dark:border-teal-500/30' },
  whatsapp_automsg_postventa_neutral: { accent: 'text-slate-600 dark:text-slate-300', dot: 'bg-slate-500', ring: 'border-slate-200 dark:border-slate-500/30' },
  whatsapp_automsg_postventa_cierre: { accent: 'text-red-600 dark:text-red-400', dot: 'bg-red-500', ring: 'border-red-200 dark:border-red-500/30' },
  whatsapp_automsg_postventa_no_contesto: { accent: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-500', ring: 'border-amber-200 dark:border-amber-500/30' },
  whatsapp_automsg_postventa_no_contesto_optout: { accent: 'text-orange-600 dark:text-orange-400', dot: 'bg-orange-500', ring: 'border-orange-200 dark:border-orange-500/30' },
  whatsapp_appointment_day_reminder: { accent: 'text-fuchsia-600 dark:text-fuchsia-400', dot: 'bg-fuchsia-500', ring: 'border-fuchsia-200 dark:border-fuchsia-500/30' },
};

// Short i18n key for a rule label (strips both prefixes).
const shortRuleKey = (k) =>
  k.replace('whatsapp_automsg_postventa_', '').replace('whatsapp_automsg_', '');

// Human-friendly fallback labels (used when an i18n entry is missing).
const RULE_LABEL_FALLBACK = {
  interested: 'Interesado',
  not_interested: 'No interesado',
  no_answer: 'No contestó',
  '1a': 'Prioridad 1A · servicio ≤ 6 mil km / 6 meses',
  '1b': 'Prioridad 1B · servicio ≤ 12 mil km / 12 meses',
  '2a': 'Prioridad 2A · próximo cada 6 mil km / 6 meses (menciona último)',
  '2b': 'Prioridad 2B · próximo cada 12 mil km / 12 meses (menciona último)',
  franja: 'Franja / Métricas 13–96 meses (genérico + promos)',
  informativo: 'Informativo (Track B) · nudge de confianza tras 60+ días sin contacto (rota 10 mensajes)',
  post_call: 'Post-llamada (genérico) · tras una llamada contestada, si el resultado no tiene regla propia',
  confirmacion: 'Confirmación · cita agendada en la llamada',
  seguimiento: 'Seguimiento · interesado sin cita',
  neutral: 'Neutral · seguimiento sin afirmar cita ni servicio vencido',
  cierre: 'Cierre · no interesado',
  no_contesto: 'No contestó / buzón',
  no_contesto_optout: 'No contestó · último de la semana (menú de baja: 1 Sí · 2 Por el momento no · 3 No deseo que me contacten)',
  appointment_day_reminder: 'Recordatorio el día de la cita (y opcional la víspera)',
};

// Placeholder chips. Postventa uses the full GM BDC variable set.
// oct20: Jocelin's verbatim openers (recordatorios-de-servicio-IA-CAMBIOS.docx) add
// {intervalo} {unidad} {costo} {sucursal} {proxServicio} {cadencia}; the legacy GM
// chips stay for tenants still on the Aug-12 approved templates.
const SALES_CHIPS = ['{nombre}', '{agente}', '{empresa}'];
const POSTVENTA_CHIPS = [
  '{cliente}',
  '{unidad}',
  '{intervalo}',
  '{costo}',
  '{sucursal}',
  '{proxServicio}',
  '{cadencia}',
  '{Consultor}',
  '{dealer}',
  '{modelo}',
  '{anioModelo}',
  '{VIN}',
  '{fechaUltser}',
  '{kmUltimo}',
];
// Informativo templates only substitute the positional trio {{1}} {{2}} {{3}}.
// Advertise ONLY those to avoid implying {modelo}/{VIN} are available here.
const INFORMATIVO_CHIPS = ['{cliente}', '{Consultor}', '{dealer}'];

// Defensive coercion of one automsg rule into { enabled, message }.
// Mirrors whatsapp_data.normalize_automsg_rule on the backend.
function normalizeAutomsgRule(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { enabled: false, message: '' };
  }
  const msg = value.message;
  return {
    enabled: !!value.enabled,
    message: typeof msg === 'string' ? msg : msg == null ? '' : String(msg),
    template_sid:
      typeof value.template_sid === 'string' ? value.template_sid : '',
  };
}

// Map-aware normalizer for the informativo (Track B) bucket. Coerces a
// (possibly partial) server value into { enabled, template_sids:{"1".."10"},
// messages:{"1".."10"} } with string keys, so all 10 idx slots always render.
// Accepts a dict OR list for the maps; falsy overrides fall back to "" so the
// UI can surface the "disabled (no SID)" affordance. Mirrors
// whatsapp_data.normalize_informativo_config on the backend (map shape only).
function normalizeInformativoConfig(value) {
  const v = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const pick = (container, idx) => {
    if (container && typeof container === 'object' && !Array.isArray(container)) {
      const hit = container[idx] != null ? container[idx] : container[Number(idx)];
      return typeof hit === 'string' ? hit : hit == null ? '' : String(hit);
    }
    if (Array.isArray(container)) {
      const hit = container[Number(idx) - 1];
      return typeof hit === 'string' ? hit : hit == null ? '' : String(hit);
    }
    return '';
  };
  const template_sids = {};
  const messages = {};
  INFORMATIVO_IDXS.forEach((idx) => {
    template_sids[idx] = pick(v.template_sids, idx).trim();
    messages[idx] = pick(v.messages, idx);
  });
  return { enabled: !!v.enabled, template_sids, messages };
}

// Day-of-appointment reminder (Roger, Oct 9). Mirrors
// appointment_day_reminder.normalize_config on the backend. The non-UI fields
// (tz, departments, sources) are carried through UNCHANGED so a save never
// resets them; the UI edits enabled / SIDs / send times / note only.
function normalizeDayReminderConfig(value) {
  const v = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const str = (x) => (typeof x === 'string' ? x : x == null ? '' : String(x));
  const out = {
    ...v,
    enabled: !!v.enabled,
    template_sid: str(v.template_sid),
    send_at: str(v.send_at) || '09:00', // Roger 2026-10-13: default morning-of hour
    day_before_enabled: !!v.day_before_enabled,
    day_before_template_sid: str(v.day_before_template_sid),
    day_before_send_at: str(v.day_before_send_at) || '18:00',
    message: str(v.message),
  };
  if (!out.tz) out.tz = 'America/Mexico_City';
  if (!Array.isArray(out.departments)) out.departments = ['postventa'];
  if (!Array.isArray(out.sources)) out.sources = ['voice', 'whatsapp'];
  return out;
}

// Was the key actually present (as an object) in the server payload?
const isRuleObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value);

// Build the canonical local config shape from a (possibly partial) server cfg.
function fromServer(cfg) {
  const c = cfg && typeof cfg === 'object' ? cfg : {};
  const tvars =
    c.whatsapp_template_vars && typeof c.whatsapp_template_vars === 'object'
      ? c.whatsapp_template_vars
      : {};
  return {
    whatsapp_enabled: !!c.whatsapp_enabled,
    waba_id: c.waba_id || '',
    whatsapp_phone_number_id: c.whatsapp_phone_number_id || '',
    whatsapp_number: c.whatsapp_number || '',
    twilio_messaging_service_sid: c.twilio_messaging_service_sid || '',
    whatsapp_fallback_template_sid: c.whatsapp_fallback_template_sid || '',
    whatsapp_template_vars: tvars,
    whatsapp_responder_enabled: !!c.whatsapp_responder_enabled,
    whatsapp_responder_agent_id: c.whatsapp_responder_agent_id || '',
    whatsapp_automsg_enabled: !!c.whatsapp_automsg_enabled,
    // POSTVENTA-PILOT-WHITELIST-aug29: per-tenant hard gate. The server returns
    // whitelist as a normalized array; the textarea edits a newline-joined
    // string, so keep both shapes coherent here.
    postventa_pilot_mode: !!c.postventa_pilot_mode,
    postventa_pilot_whitelist: Array.isArray(c.postventa_pilot_whitelist)
      ? c.postventa_pilot_whitelist
      : [],
    // PER-TENANT CAMPAIGN CALLING WINDOW (labour-hours dialing, Sep 3). The
    // server returns a normalized {start,end,tz,days} object or null (unset).
    // Keep null as null so an unsaved tenant stays inert (legacy per-campaign
    // hours) instead of being forced into a window.
    campaign_calling_window:
      c.campaign_calling_window && typeof c.campaign_calling_window === 'object'
        ? c.campaign_calling_window
        : null,
    ...Object.fromEntries(
      ALL_RULE_KEYS.map((k) => [k, normalizeAutomsgRule(c[k])])
    ),
    [INFORMATIVO_KEY]: normalizeInformativoConfig(c[INFORMATIVO_KEY]),
    // Optional post-call / reminder rules: ONLY materialize the keys the server
    // returned (see POSTVENTA_POST_CALL_RULE_KEYS contract). Absent keys stay
    // absent (undefined) so they are not sent back on save.
    ...Object.fromEntries(
      POSTVENTA_POST_CALL_RULE_KEYS.filter((k) => isRuleObject(c[k])).map((k) => [
        k,
        normalizeAutomsgRule(c[k]),
      ])
    ),
    ...(isRuleObject(c[DAY_REMINDER_KEY])
      ? { [DAY_REMINDER_KEY]: normalizeDayReminderConfig(c[DAY_REMINDER_KEY]) }
      : {}),
  };
}

// Substitute the sample chips so the admin sees a realistic preview ("Juan").
function renderPreview(message) {
  const text = typeof message === 'string' ? message : '';
  return text
    .replace(/\{nombre\}/g, 'Juan')
    .replace(/\{cliente\}/g, 'Juan Pérez')
    .replace(/\{agente\}/g, 'Ana')
    .replace(/\{Consultor\}/g, 'Ana Torres')
    .replace(/\{dealer\}/g, 'Villahermosa')
    .replace(/\{modelo\}/g, 'Aveo')
    .replace(/\{anioModelo\}/g, '2023')
    .replace(/\{VIN\}/g, '1G1F91R6XP0121960')
    .replace(/\{fechaUltser\}/g, '2025-12-10')
    .replace(/\{kmUltimo\}/g, '18,500')
    // oct20: Jocelin's verbatim-opener placeholders (branch naming her way: Deportiva).
    .replace(/\{unidad\}/g, 'Aveo 2023, serie 1G1F91R6XP0121960')
    .replace(/\{intervalo\}/g, '6 mil km o 6 meses')
    .replace(/\{costo\}/g, 'con costo')
    .replace(/\{sucursal\}/g, 'Deportiva')
    .replace(/\{proxServicio\}/g, '36 mil km')
    .replace(/\{cadencia\}/g, '12 mil km o 12 meses')
    .replace(/\{empresa\}/g, 'Qomplement');
}

function WhatsAppConfig() {
  const { t } = useTranslation();
  // WABA/connection config is admin-only. Reuse the same auth source as the
  // rest of the admin-gated UI (AuthContext.isAdmin -> ['admin','super_admin']).
  const { isAdmin } = useAuth();
  const canEditWaba = typeof isAdmin === 'function' ? isAdmin() : false;

  const [cfg, setCfg] = useState(() => fromServer(null));
  const [automsgTab, setAutomsgTab] = useState('sales'); // 'sales' | 'postventa'
  const [templateVarsText, setTemplateVarsText] = useState('');
  // POSTVENTA-PILOT-WHITELIST-aug29: the whitelist is edited as free text (one
  // number per line); parsed to an array on save. Backend also accepts a raw
  // string, but we normalize client-side for a clean payload + optimistic UI.
  const [pilotWhitelistText, setPilotWhitelistText] = useState('');
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get('/tenants/me/whatsapp-config');
        if (cancelled) return;
        const next = fromServer(res?.data);
        setCfg(next);
        setTemplateVarsText(
          Object.keys(next.whatsapp_template_vars).length
            ? JSON.stringify(next.whatsapp_template_vars, null, 2)
            : ''
        );
        setPilotWhitelistText((next.postventa_pilot_whitelist || []).join('\n'));
      } catch (error) {
        if (!cancelled && error?.response?.status !== 404) {
          toast.error(t('settings.whatsappConfig.loadError'));
        }
        // eslint-disable-next-line no-console
        console.error('Error fetching WhatsApp config:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the tenant-scoped agents to populate the responder <select>.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/agents');
        if (cancelled) return;
        setAgents(Array.isArray(res?.data) ? res.data : []);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching agents for WhatsApp responder:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setField = (field, value) =>
    setCfg((prev) => ({ ...prev, [field]: value }));

  // PER-TENANT CAMPAIGN CALLING WINDOW (labour-hours dialing, Sep 3). A single
  // checkbox enables/disables the window; when enabled we edit start/end/tz/days
  // on a canonical object. Disabling clears it to null (unset => dialer keeps
  // legacy per-campaign hours).
  const CALLING_WINDOW_DEFAULT = {
    start: '09:00',
    end: '18:00',
    tz: 'America/Mexico_City',
    days: [0, 1, 2, 3, 4], // Mon-Fri
  };
  const setCallingWindowEnabled = (enabled) =>
    setCfg((prev) => ({
      ...prev,
      campaign_calling_window: enabled
        ? { ...CALLING_WINDOW_DEFAULT, ...(prev.campaign_calling_window || {}) }
        : null,
    }));
  const setCallingWindowField = (field, value) =>
    setCfg((prev) => ({
      ...prev,
      campaign_calling_window: {
        ...CALLING_WINDOW_DEFAULT,
        ...(prev.campaign_calling_window || {}),
        [field]: value,
      },
    }));
  const toggleCallingWindowDay = (day) =>
    setCfg((prev) => {
      const cur = { ...CALLING_WINDOW_DEFAULT, ...(prev.campaign_calling_window || {}) };
      const days = Array.isArray(cur.days) ? cur.days.slice() : [];
      const idx = days.indexOf(day);
      if (idx >= 0) days.splice(idx, 1);
      else days.push(day);
      days.sort((a, b) => a - b);
      return { ...prev, campaign_calling_window: { ...cur, days } };
    });

  const setRuleField = (ruleKey, field, value) =>
    setCfg((prev) => ({
      ...prev,
      [ruleKey]: { ...normalizeAutomsgRule(prev[ruleKey]), [field]: value },
    }));

  // Day-of-appointment reminder field setter (keeps tz/departments/sources).
  const setDayReminderField = (field, value) =>
    setCfg((prev) => ({
      ...prev,
      [DAY_REMINDER_KEY]: {
        ...normalizeDayReminderConfig(prev[DAY_REMINDER_KEY]),
        [field]: value,
      },
    }));

  // Informativo bucket-level enabled toggle.
  const setInformativoEnabled = (value) =>
    setCfg((prev) => ({
      ...prev,
      [INFORMATIVO_KEY]: {
        ...normalizeInformativoConfig(prev[INFORMATIVO_KEY]),
        enabled: !!value,
      },
    }));

  // Per-idx setter for either the template_sids or messages map.
  const setInformativoMapField = (mapName, idx, value) =>
    setCfg((prev) => {
      const cur = normalizeInformativoConfig(prev[INFORMATIVO_KEY]);
      return {
        ...prev,
        [INFORMATIVO_KEY]: {
          ...cur,
          [mapName]: { ...cur[mapName], [idx]: value },
        },
      };
    });

  const handleSave = async () => {
    // Parse template vars JSON (empty == {}).
    let templateVars = {};
    const raw = (templateVarsText || '').trim();
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('not an object');
        }
        templateVars = parsed;
      } catch (e) {
        toast.error(t('settings.whatsappConfig.invalidTemplateVars'));
        return;
      }
    }

    const payload = {
      whatsapp_enabled: !!cfg.whatsapp_enabled,
      waba_id: cfg.waba_id || '',
      whatsapp_phone_number_id: cfg.whatsapp_phone_number_id || '',
      whatsapp_number: cfg.whatsapp_number || '',
      twilio_messaging_service_sid: cfg.twilio_messaging_service_sid || '',
      whatsapp_fallback_template_sid: cfg.whatsapp_fallback_template_sid || '',
      whatsapp_template_vars: templateVars,
      whatsapp_responder_enabled: !!cfg.whatsapp_responder_enabled,
      whatsapp_responder_agent_id: cfg.whatsapp_responder_agent_id || '',
      whatsapp_automsg_enabled: !!cfg.whatsapp_automsg_enabled,
      // POSTVENTA-PILOT-WHITELIST-aug29: send the toggle + parsed whitelist
      // (one number per line -> array). Backend re-normalizes to E.164 anyway.
      postventa_pilot_mode: !!cfg.postventa_pilot_mode,
      postventa_pilot_whitelist: (pilotWhitelistText || '')
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean),
      // PER-TENANT CAMPAIGN CALLING WINDOW (labour-hours dialing, Sep 3). Send
      // the current window object (or null to clear). Backend re-normalizes and
      // maps an empty/None window back to "unset" (legacy per-campaign hours).
      campaign_calling_window: cfg.campaign_calling_window || null,
      ...Object.fromEntries(
        ALL_RULE_KEYS.map((k) => [k, normalizeAutomsgRule(cfg[k])])
      ),
      [INFORMATIVO_KEY]: normalizeInformativoConfig(cfg[INFORMATIVO_KEY]),
      // Optional post-call / reminder rules: send ONLY the keys present in
      // state (returned by GET or edited here) as full round-tripped objects.
      // Absent keys are omitted entirely (never sent as empty strings) so the
      // backend leaves the stored/default value untouched.
      ...Object.fromEntries(
        POSTVENTA_POST_CALL_RULE_KEYS.filter((k) => cfg[k] !== undefined).map((k) => [
          k,
          normalizeAutomsgRule(cfg[k]),
        ])
      ),
      ...(cfg[DAY_REMINDER_KEY] !== undefined
        ? { [DAY_REMINDER_KEY]: normalizeDayReminderConfig(cfg[DAY_REMINDER_KEY]) }
        : {}),
    };

    try {
      setSaving(true);
      const res = await api.put('/tenants/me/whatsapp-config', payload);
      const next = fromServer(res?.data || payload);
      setCfg(next);
      setTemplateVarsText(
        Object.keys(next.whatsapp_template_vars).length
          ? JSON.stringify(next.whatsapp_template_vars, null, 2)
          : ''
      );
      setPilotWhitelistText((next.postventa_pilot_whitelist || []).join('\n'));
      toast.success(t('settings.whatsappConfig.saved'));
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || t('settings.whatsappConfig.saveError'));
      // eslint-disable-next-line no-console
      console.error('Error saving WhatsApp config:', error);
    } finally {
      setSaving(false);
    }
  };

  const masterOn = !!cfg.whatsapp_automsg_enabled;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ---------------- Card B — Automatic messages ---------------- */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-success-600" />
            {t('whatsapp.config.automsg.title')}
          </h3>
        </div>
        <p className="mb-4 text-xs" style={{ color: 'var(--muted)' }}>
          {t('whatsapp.config.automsg.help')}
        </p>

        {/* Master toggle — greys all 3 rules when off */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <label
              htmlFor="wa_automsg_enabled"
              className="block text-sm font-medium text-foreground"
            >
              {t('whatsapp.config.automsg.masterLabel')}
            </label>
            <p className="text-xs" style={{ color: 'var(--muted)' }}>
              {t('whatsapp.config.automsg.masterHelp')}
            </p>
          </div>
          <input
            type="checkbox"
            id="wa_automsg_enabled"
            checked={masterOn}
            onChange={(e) => setField('whatsapp_automsg_enabled', e.target.checked)}
            className="h-5 w-5"
          />
        </div>

        {/* Ventas / Postventa tab switcher */}
        <div className="mb-4 inline-flex rounded-md border border-border p-0.5">
          {[
            ['sales', t('whatsapp.config.automsg.tabSales', 'Ventas')],
            ['postventa', t('whatsapp.config.automsg.tabPostventa', 'Postventa')],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setAutomsgTab(key)}
              className={`rounded px-3 py-1.5 text-sm font-medium ${
                automsgTab === key
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Reference chips (postventa exposes {modelo}) */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs" style={{ color: 'var(--muted)' }}>
            {t('whatsapp.config.automsg.chipsLabel')}:
          </span>
          {(automsgTab === 'postventa' ? POSTVENTA_CHIPS : SALES_CHIPS).map((chip) => (
            <code
              key={chip}
              className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-200"
            >
              {chip}
            </code>
          ))}
        </div>

        {/* Informativo uses only the positional trio; other tabs keep their sets. */}
        <div className="space-y-4">
          {(automsgTab === 'postventa' ? POSTVENTA_RULE_KEYS : SALES_RULE_KEYS).map((ruleKey) => {
            const rule = normalizeAutomsgRule(cfg[ruleKey]);
            const theme = RULE_THEME[ruleKey];
            // Pure derived render: a row is greyed when master OFF or rule OFF.
            const dimmed = !masterOn || !rule.enabled;
            const shortKey = shortRuleKey(ruleKey);
            return (
              <div
                key={ruleKey}
                className={`rounded-lg border p-3 transition-opacity ${theme.ring} ${
                  dimmed ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`flex items-center gap-2 text-sm font-medium ${theme.accent}`}
                  >
                    <span className={`h-2.5 w-2.5 rounded-full ${theme.dot}`} />
                    {t(`whatsapp.config.automsg.rules.${shortKey}`, RULE_LABEL_FALLBACK[shortKey] || shortKey)}
                  </span>
                  <input
                    type="checkbox"
                    aria-label={t(`whatsapp.config.automsg.rules.${shortKey}`, RULE_LABEL_FALLBACK[shortKey] || shortKey)}
                    checked={!!rule.enabled}
                    disabled={!masterOn}
                    onChange={(e) =>
                      setRuleField(ruleKey, 'enabled', e.target.checked)
                    }
                    className="h-5 w-5 disabled:cursor-not-allowed"
                  />
                </div>

                <textarea
                  value={rule.message}
                  disabled={!masterOn || !rule.enabled}
                  onChange={(e) => setRuleField(ruleKey, 'message', e.target.value)}
                  className="input-field mt-3 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                  rows={3}
                  placeholder={t('whatsapp.config.automsg.messagePlaceholder')}
                />

                {/* Live preview ("Juan") — pure derived render */}
                {rule.message.trim() ? (
                  <div className="mt-2 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-gray-700/50 dark:text-gray-300">
                    <span className="mr-1 font-medium">
                      {t('whatsapp.config.automsg.previewLabel')}:
                    </span>
                    <span className="whitespace-pre-wrap break-words">
                      {renderPreview(rule.message)}
                    </span>
                  </div>
                ) : null}
              </div>
            );
          })}

          {/* -------- Informativo (Track B) rotating bucket --------
              Distinct map shape: bucket-level enabled toggle + 10 idx rows,
              each with a template SID input + a per-idx message textarea.
              Empty SID for an idx => "disabled (no SID)" (backend fail-safe). */}
          {automsgTab === 'postventa' ? (() => {
            const inf = normalizeInformativoConfig(cfg[INFORMATIVO_KEY]);
            const theme = RULE_THEME[INFORMATIVO_KEY];
            const infDimmed = !masterOn || !inf.enabled;
            const infLabel = t(
              'whatsapp.config.automsg.rules.informativo',
              RULE_LABEL_FALLBACK.informativo
            );
            return (
              <div
                key={INFORMATIVO_KEY}
                className={`rounded-lg border p-3 transition-opacity ${theme.ring} ${
                  !masterOn ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`flex items-center gap-2 text-sm font-medium ${theme.accent}`}>
                    <span className={`h-2.5 w-2.5 rounded-full ${theme.dot}`} />
                    {infLabel}
                  </span>
                  <input
                    type="checkbox"
                    aria-label={infLabel}
                    checked={!!inf.enabled}
                    disabled={!masterOn}
                    onChange={(e) => setInformativoEnabled(e.target.checked)}
                    className="h-5 w-5 disabled:cursor-not-allowed"
                  />
                </div>

                <p className="mt-1 text-[11px]" style={{ color: 'var(--muted)' }}>
                  {t(
                    'whatsapp.config.automsg.informativo.help',
                    'Rota 10 mensajes. Cada índice usa su propio SID de plantilla aprobado; sin SID, ese índice queda deshabilitado.'
                  )}
                </p>

                {/* Informativo-only chips (positional trio). */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-[11px]" style={{ color: 'var(--muted)' }}>
                    {t('whatsapp.config.automsg.chipsLabel')}:
                  </span>
                  {INFORMATIVO_CHIPS.map((chip) => (
                    <code
                      key={chip}
                      className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                    >
                      {chip}
                    </code>
                  ))}
                </div>

                <div className={`mt-3 space-y-3 transition-opacity ${infDimmed ? 'opacity-60' : ''}`}>
                  {INFORMATIVO_IDXS.map((idx) => {
                    const sid = inf.template_sids[idx] || '';
                    const msg = inf.messages[idx] || '';
                    const noSid = !sid.trim();
                    return (
                      <div
                        key={idx}
                        className="rounded-md border border-border/60 p-2.5"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-foreground">
                            {t('whatsapp.config.automsg.informativo.idxLabel', 'Mensaje')} #{idx}
                            <span className="ml-1 text-[10px]" style={{ color: 'var(--muted)' }}>
                              ({INFORMATIVO_IDX_HINT[idx]})
                            </span>
                          </span>
                          {noSid ? (
                            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
                              {t('whatsapp.config.automsg.informativo.noSid', 'deshabilitado (sin SID)')}
                            </span>
                          ) : null}
                        </div>
                        <input
                          type="text"
                          value={sid}
                          disabled={!masterOn || !inf.enabled}
                          onChange={(e) => setInformativoMapField('template_sids', idx, e.target.value)}
                          className="input-field mt-2 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                          placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          aria-label={`template SID #${idx}`}
                        />
                        <textarea
                          value={msg}
                          disabled={!masterOn || !inf.enabled}
                          onChange={(e) => setInformativoMapField('messages', idx, e.target.value)}
                          className="input-field mt-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                          rows={3}
                          placeholder={t('whatsapp.config.automsg.messagePlaceholder')}
                          aria-label={`message #${idx}`}
                        />
                        {msg.trim() ? (
                          <div className="mt-2 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-gray-700/50 dark:text-gray-300">
                            <span className="mr-1 font-medium">
                              {t('whatsapp.config.automsg.previewLabel')}:
                            </span>
                            <span className="whitespace-pre-wrap break-words">
                              {renderPreview(msg)}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })() : null}

          {/* -------- Mensajes post-llamada / confirmación (template-driven) --------
              Fired AFTER an answered postventa call (per outcome: confirmación /
              seguimiento / neutral / cierre / no_contestó, generic post_call as
              fallback) plus the day-of-appointment reminder. Each needs an
              approved Twilio Content SID: the text here is the human note /
              fallback body, the customer receives the APPROVED template. */}
          {automsgTab === 'postventa' ? (
            <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }} data-testid="postventa-post-call-group">
              <h4 className="text-sm font-semibold text-foreground">
                {t(
                  'whatsapp.config.automsg.postCall.title',
                  'Mensajes post-llamada / confirmación'
                )}
              </h4>
              <p className="mb-3 mt-1 text-[11px]" style={{ color: 'var(--muted)' }}>
                {t(
                  'whatsapp.config.automsg.postCall.help',
                  'Se envían después de una llamada de Postventa según el resultado (confirmación cuando hay cita, seguimiento, neutral, cierre o no contestó; “post-llamada” es el genérico de respaldo). Cada regla usa una plantilla aprobada de WhatsApp (SID): el cliente recibe la plantilla aprobada; el texto de abajo es la nota/respaldo. Sin SID, la regla queda deshabilitada.'
                )}
              </p>

              <div className="space-y-4">
                {POSTVENTA_POST_CALL_RULE_KEYS.map((ruleKey) => {
                  const rule = normalizeAutomsgRule(cfg[ruleKey]);
                  const theme = RULE_THEME[ruleKey];
                  const dimmed = !masterOn || !rule.enabled;
                  const shortKey = shortRuleKey(ruleKey);
                  const label = t(
                    `whatsapp.config.automsg.rules.${shortKey}`,
                    RULE_LABEL_FALLBACK[shortKey] || shortKey
                  );
                  const noSid = !(rule.template_sid || '').trim();
                  return (
                    <div
                      key={ruleKey}
                      data-rule-key={ruleKey}
                      className={`rounded-lg border p-3 transition-opacity ${theme.ring} ${
                        dimmed ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`flex items-center gap-2 text-sm font-medium ${theme.accent}`}>
                          <span className={`h-2.5 w-2.5 rounded-full ${theme.dot}`} />
                          {label}
                          {noSid ? (
                            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-normal text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
                              {t('whatsapp.config.automsg.postCall.noSid', 'sin SID · no se envía')}
                            </span>
                          ) : null}
                        </span>
                        <input
                          type="checkbox"
                          aria-label={label}
                          checked={!!rule.enabled}
                          disabled={!masterOn}
                          onChange={(e) => setRuleField(ruleKey, 'enabled', e.target.checked)}
                          className="h-5 w-5 disabled:cursor-not-allowed"
                        />
                      </div>

                      <input
                        type="text"
                        value={rule.template_sid || ''}
                        disabled={!masterOn || !rule.enabled}
                        onChange={(e) => setRuleField(ruleKey, 'template_sid', e.target.value)}
                        className="input-field mt-2 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                        placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        aria-label={`${label} · template SID`}
                      />

                      <textarea
                        value={rule.message}
                        disabled={!masterOn || !rule.enabled}
                        onChange={(e) => setRuleField(ruleKey, 'message', e.target.value)}
                        className="input-field mt-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                        rows={3}
                        placeholder={t('whatsapp.config.automsg.messagePlaceholder')}
                        aria-label={`${label} · message`}
                      />

                      {rule.message.trim() ? (
                        <div className="mt-2 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-gray-700/50 dark:text-gray-300">
                          <span className="mr-1 font-medium">
                            {t('whatsapp.config.automsg.previewLabel')}:
                          </span>
                          <span className="whitespace-pre-wrap break-words">
                            {renderPreview(rule.message)}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  );
                })}

                {/* Day-of-appointment reminder (morning of the cita + optional
                    evening-before). Distinct shape: day-of SID + hour, day-before
                    toggle + its OWN SID + hour. tz/departments/sources are kept
                    as stored (not edited here). */}
                {(() => {
                  const dr = normalizeDayReminderConfig(cfg[DAY_REMINDER_KEY]);
                  const theme = RULE_THEME[DAY_REMINDER_KEY];
                  const dimmed = !masterOn || !dr.enabled;
                  const label = t(
                    'whatsapp.config.automsg.rules.appointment_day_reminder',
                    RULE_LABEL_FALLBACK.appointment_day_reminder
                  );
                  const noSid = !(dr.template_sid || '').trim();
                  return (
                    <div
                      data-rule-key={DAY_REMINDER_KEY}
                      className={`rounded-lg border p-3 transition-opacity ${theme.ring} ${
                        dimmed ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`flex items-center gap-2 text-sm font-medium ${theme.accent}`}>
                          <span className={`h-2.5 w-2.5 rounded-full ${theme.dot}`} />
                          {label}
                          {noSid ? (
                            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-normal text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
                              {t('whatsapp.config.automsg.postCall.noSid', 'sin SID · no se envía')}
                            </span>
                          ) : null}
                        </span>
                        <input
                          type="checkbox"
                          id="wa_day_reminder_enabled"
                          aria-label={label}
                          checked={!!dr.enabled}
                          disabled={!masterOn}
                          onChange={(e) => setDayReminderField('enabled', e.target.checked)}
                          className="h-5 w-5 disabled:cursor-not-allowed"
                        />
                      </div>
                      <p className="mt-1 text-[11px]" style={{ color: 'var(--muted)' }}>
                        {t(
                          'whatsapp.config.automsg.dayReminder.help',
                          'Recordatorio por WhatsApp la mañana de la cita (plantilla aprobada de utilidad). Opcionalmente otro la víspera con su propia plantilla. Zona horaria: '
                        )}
                        <code>{dr.tz}</code>
                      </p>

                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                        <div className="sm:col-span-2">
                          <label htmlFor="wa_day_reminder_template_sid" className="block text-[11px] font-medium text-foreground">
                            {t('whatsapp.config.automsg.dayReminder.sidLabel', 'SID plantilla (día de la cita)')}
                          </label>
                          <input
                            type="text"
                            id="wa_day_reminder_template_sid"
                            value={dr.template_sid}
                            disabled={!masterOn || !dr.enabled}
                            onChange={(e) => setDayReminderField('template_sid', e.target.value)}
                            className="input-field mt-1 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                            placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          />
                        </div>
                        <div>
                          <label htmlFor="wa_day_reminder_send_at" className="block text-[11px] font-medium text-foreground">
                            {t('whatsapp.config.automsg.dayReminder.sendAtLabel', 'Hora de envío')}
                          </label>
                          <input
                            type="time"
                            id="wa_day_reminder_send_at"
                            value={dr.send_at}
                            disabled={!masterOn || !dr.enabled}
                            onChange={(e) => setDayReminderField('send_at', e.target.value)}
                            className="input-field mt-1 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                          />
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between">
                        <label htmlFor="wa_day_reminder_day_before_enabled" className="text-xs font-medium text-foreground">
                          {t('whatsapp.config.automsg.dayReminder.dayBeforeLabel', 'También recordar la víspera (tarde anterior)')}
                        </label>
                        <input
                          type="checkbox"
                          id="wa_day_reminder_day_before_enabled"
                          checked={!!dr.day_before_enabled}
                          disabled={!masterOn || !dr.enabled}
                          onChange={(e) => setDayReminderField('day_before_enabled', e.target.checked)}
                          className="h-4 w-4 disabled:cursor-not-allowed"
                        />
                      </div>
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                        <div className="sm:col-span-2">
                          <label htmlFor="wa_day_reminder_day_before_template_sid" className="block text-[11px] font-medium text-foreground">
                            {t('whatsapp.config.automsg.dayReminder.dayBeforeSidLabel', 'SID plantilla (víspera, “mañana”)')}
                          </label>
                          <input
                            type="text"
                            id="wa_day_reminder_day_before_template_sid"
                            value={dr.day_before_template_sid}
                            disabled={!masterOn || !dr.enabled || !dr.day_before_enabled}
                            onChange={(e) => setDayReminderField('day_before_template_sid', e.target.value)}
                            className="input-field mt-1 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                            placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          />
                        </div>
                        <div>
                          <label htmlFor="wa_day_reminder_day_before_send_at" className="block text-[11px] font-medium text-foreground">
                            {t('whatsapp.config.automsg.dayReminder.dayBeforeSendAtLabel', 'Hora (víspera)')}
                          </label>
                          <input
                            type="time"
                            id="wa_day_reminder_day_before_send_at"
                            value={dr.day_before_send_at}
                            disabled={!masterOn || !dr.enabled || !dr.day_before_enabled}
                            onChange={(e) => setDayReminderField('day_before_send_at', e.target.value)}
                            className="input-field mt-1 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                          />
                        </div>
                      </div>

                      <textarea
                        value={dr.message}
                        disabled={!masterOn || !dr.enabled}
                        onChange={(e) => setDayReminderField('message', e.target.value)}
                        className="input-field mt-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                        rows={2}
                        placeholder={t('whatsapp.config.automsg.dayReminder.notePlaceholder', 'Nota interna / respaldo del recordatorio')}
                        aria-label={`${label} · message`}
                      />
                      {dr.message.trim() ? (
                        <div className="mt-2 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-gray-700/50 dark:text-gray-300">
                          <span className="mr-1 font-medium">
                            {t('whatsapp.config.automsg.previewLabel')}:
                          </span>
                          <span className="whitespace-pre-wrap break-words">
                            {renderPreview(dr.message)}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* -------- Card P — Postventa PILOT / WHITELIST mode (Jocelin) -------- */}
      {/* Per-tenant HARD gate: when ON, EVERY postventa outbound (send-due
          templates, inbox responder template replies, postventa calls) can only
          reach numbers on this allowlist. Non-whitelisted inbound still gets a
          polite AI reply but NO cita/proactive follow-up. Enforced server-side
          at the send primitive; this UI just edits the config. */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-amber-500" />
            {t('whatsapp.config.pilot.title', 'Modo piloto Postventa (solo lista blanca)')}
          </h3>
        </div>
        <p className="mb-4 text-xs" style={{ color: 'var(--muted)' }}>
          {t(
            'whatsapp.config.pilot.help',
            'Cuando está activo, TODOS los envíos de Postventa (recordatorios, respuestas del asistente por plantilla y llamadas) solo llegan a los números de la lista. El resto se omite y se cuenta, nunca se envía. Ideal para pruebas con números del personal del distribuidor.'
          )}
        </p>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label
                htmlFor="postventa_pilot_mode"
                className="block text-sm font-medium text-foreground"
              >
                {t('whatsapp.config.pilot.modeLabel', 'Activar modo piloto (lista blanca)')}
              </label>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                {t(
                  'whatsapp.config.pilot.modeHelp',
                  'OFF = comportamiento normal (envía a todos). ON = solo a la lista de abajo.'
                )}
              </p>
            </div>
            <input
              type="checkbox"
              id="postventa_pilot_mode"
              checked={!!cfg.postventa_pilot_mode}
              onChange={(e) => setField('postventa_pilot_mode', e.target.checked)}
              disabled={!canEditWaba}
              className="h-5 w-5 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          <div>
            <label
              htmlFor="postventa_pilot_whitelist"
              className="block text-sm font-medium text-foreground"
            >
              {t('whatsapp.config.pilot.whitelistLabel', 'Números permitidos (E.164, uno por línea)')}
            </label>
            <textarea
              id="postventa_pilot_whitelist"
              value={pilotWhitelistText}
              onChange={(e) => setPilotWhitelistText(e.target.value)}
              disabled={!canEditWaba || !cfg.postventa_pilot_mode}
              className="input-field mt-1 font-mono text-xs disabled:cursor-not-allowed disabled:opacity-60"
              rows={5}
              placeholder={'+5215512345678\n+5215598765432'}
            />
            <p className="mt-2 text-xs" style={{ color: 'var(--muted)' }}>
              {t(
                'whatsapp.config.pilot.whitelistHelp',
                'Con el modo activo y la lista vacía NO se envía a nadie (fail-safe). Los números se normalizan a E.164 al guardar.'
              )}
            </p>
          </div>

          {/* PER-TENANT CAMPAIGN CALLING WINDOW (labour-hours dialing, Sep 3) */}
          <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center justify-between">
              <div>
                <label
                  htmlFor="campaign_calling_window_enabled"
                  className="block text-sm font-medium text-foreground"
                >
                  {t(
                    'whatsapp.config.callingWindow.enableLabel',
                    'Horario laboral de marcación (campañas)'
                  )}
                </label>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  {t(
                    'whatsapp.config.callingWindow.enableHelp',
                    'OFF = cada campaña usa su propio horario. ON = las campañas sin horario propio marcan solo en esta ventana.'
                  )}
                </p>
              </div>
              <input
                type="checkbox"
                id="campaign_calling_window_enabled"
                checked={!!cfg.campaign_calling_window}
                onChange={(e) => setCallingWindowEnabled(e.target.checked)}
                disabled={!canEditWaba}
                className="h-5 w-5 disabled:cursor-not-allowed disabled:opacity-60"
              />
            </div>

            {cfg.campaign_calling_window ? (
              <div className="mt-3 space-y-3">
                <div className="flex flex-wrap gap-3">
                  <div>
                    <label className="block text-xs text-foreground">
                      {t('whatsapp.config.callingWindow.startLabel', 'Inicio')}
                    </label>
                    <input
                      type="time"
                      value={cfg.campaign_calling_window.start || '09:00'}
                      onChange={(e) => setCallingWindowField('start', e.target.value)}
                      disabled={!canEditWaba}
                      className="input-field mt-1 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-foreground">
                      {t('whatsapp.config.callingWindow.endLabel', 'Fin')}
                    </label>
                    <input
                      type="time"
                      value={cfg.campaign_calling_window.end || '18:00'}
                      onChange={(e) => setCallingWindowField('end', e.target.value)}
                      disabled={!canEditWaba}
                      className="input-field mt-1 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                    />
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <label className="block text-xs text-foreground">
                      {t('whatsapp.config.callingWindow.tzLabel', 'Zona horaria (IANA)')}
                    </label>
                    <input
                      type="text"
                      value={cfg.campaign_calling_window.tz || 'America/Mexico_City'}
                      onChange={(e) => setCallingWindowField('tz', e.target.value)}
                      disabled={!canEditWaba}
                      className="input-field mt-1 font-mono text-xs disabled:cursor-not-allowed disabled:opacity-60"
                      placeholder="America/Mexico_City"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-foreground mb-1">
                    {t('whatsapp.config.callingWindow.daysLabel', 'Días permitidos')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      ['L', 0], ['M', 1], ['Mi', 2], ['J', 3],
                      ['V', 4], ['S', 5], ['D', 6],
                    ].map(([label, day]) => {
                      const days = Array.isArray(cfg.campaign_calling_window.days)
                        ? cfg.campaign_calling_window.days
                        : [];
                      const on = days.includes(day);
                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => toggleCallingWindowDay(day)}
                          disabled={!canEditWaba}
                          className={`h-8 w-8 rounded-full text-xs font-medium disabled:cursor-not-allowed disabled:opacity-60 ${
                            on
                              ? 'bg-success-600 text-white'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                          aria-pressed={on}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-xs" style={{ color: 'var(--muted)' }}>
                    {t(
                      'whatsapp.config.callingWindow.help',
                      'Los contactos fuera de la ventana quedan pendientes y se marcan cuando la ventana abre (no se pierden). Sábado queda PENDIENTE por confirmar.'
                    )}
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* ---------------- Card A — WABA connection ---------------- */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <PhoneIcon className="h-5 w-5 mr-2 text-success-600" />
            {t('settings.whatsappConfig.sectionTitle')}
          </h3>
        </div>
        <p className="mb-4 text-xs" style={{ color: 'var(--muted)' }}>
          {t('settings.whatsappConfig.helpText')}
        </p>

        {!canEditWaba ? (
          <p
            className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-500/10 dark:text-amber-400"
          >
            {t('settings.whatsappConfig.adminOnlyNote')}
          </p>
        ) : null}

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label
                htmlFor="wa_enabled"
                className="block text-sm font-medium text-foreground"
              >
                {t('settings.whatsappConfig.enabledLabel')}
              </label>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                {t('settings.whatsappConfig.enabledHelp')}
              </p>
            </div>
            <input
              type="checkbox"
              id="wa_enabled"
              checked={!!cfg.whatsapp_enabled}
              onChange={(e) => setField('whatsapp_enabled', e.target.checked)}
              disabled={!canEditWaba}
              className="h-5 w-5 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          {/* Inbound AI auto-responder toggle + agent picker */}
          <div className="flex items-center justify-between">
            <div>
              <label
                htmlFor="wa_responder_enabled"
                className="block text-sm font-medium text-foreground"
              >
                {t('settings.whatsappConfig.responderLabel', 'AI auto-responder')}
              </label>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                {t(
                  'settings.whatsappConfig.responderHelp',
                  'Automatically answer inbound WhatsApp messages with the selected agent (only inside the 24h window).'
                )}
              </p>
            </div>
            <input
              type="checkbox"
              id="wa_responder_enabled"
              checked={!!cfg.whatsapp_responder_enabled}
              onChange={(e) => setField('whatsapp_responder_enabled', e.target.checked)}
              disabled={!canEditWaba}
              className="h-5 w-5 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          <div>
            <label
              htmlFor="wa_responder_agent_id"
              className="block text-sm font-medium text-foreground"
            >
              {t('settings.whatsappConfig.responderAgentLabel', 'Responder agent')}
            </label>
            <select
              id="wa_responder_agent_id"
              value={cfg.whatsapp_responder_agent_id}
              onChange={(e) => setField('whatsapp_responder_agent_id', e.target.value)}
              disabled={!canEditWaba || !cfg.whatsapp_responder_enabled}
              className="input-field mt-1 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <option value="">
                {t('settings.whatsappConfig.responderAgentPlaceholder', 'Select an agent…')}
              </option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name || a.id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="wa_waba_id"
              className="block text-sm font-medium text-foreground"
            >
              {t('settings.whatsappConfig.wabaId')}
            </label>
            <input
              type="text"
              id="wa_waba_id"
              value={cfg.waba_id}
              onChange={(e) => setField('waba_id', e.target.value)}
              disabled={!canEditWaba}
              className="input-field mt-1 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          <div>
            <label
              htmlFor="wa_phone_number_id"
              className="block text-sm font-medium text-foreground"
            >
              {t('settings.whatsappConfig.phoneNumberId')}
            </label>
            <input
              type="text"
              id="wa_phone_number_id"
              value={cfg.whatsapp_phone_number_id}
              onChange={(e) => setField('whatsapp_phone_number_id', e.target.value)}
              disabled={!canEditWaba}
              className="input-field mt-1 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          <div>
            <label
              htmlFor="wa_number"
              className="block text-sm font-medium text-foreground"
            >
              {t('settings.whatsappConfig.whatsappNumber')}
            </label>
            <input
              type="tel"
              id="wa_number"
              value={cfg.whatsapp_number}
              onChange={(e) => setField('whatsapp_number', e.target.value)}
              disabled={!canEditWaba}
              className="input-field mt-1 disabled:cursor-not-allowed disabled:opacity-60"
              placeholder={t('settings.whatsappConfig.whatsappNumberPlaceholder')}
            />
          </div>

          <div>
            <label
              htmlFor="wa_messaging_service_sid"
              className="block text-sm font-medium text-foreground"
            >
              {t('settings.whatsappConfig.messagingServiceSid')}
            </label>
            <input
              type="text"
              id="wa_messaging_service_sid"
              value={cfg.twilio_messaging_service_sid}
              onChange={(e) =>
                setField('twilio_messaging_service_sid', e.target.value)
              }
              disabled={!canEditWaba}
              className="input-field mt-1 disabled:cursor-not-allowed disabled:opacity-60"
              placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            />
          </div>

          <div>
            <label
              htmlFor="wa_fallback_template_sid"
              className="block text-sm font-medium text-foreground"
            >
              {t('settings.whatsappConfig.fallbackTemplateSid')}
            </label>
            <input
              type="text"
              id="wa_fallback_template_sid"
              value={cfg.whatsapp_fallback_template_sid}
              onChange={(e) =>
                setField('whatsapp_fallback_template_sid', e.target.value)
              }
              disabled={!canEditWaba}
              className="input-field mt-1 disabled:cursor-not-allowed disabled:opacity-60"
              placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            />
          </div>

          <div>
            <label
              htmlFor="wa_template_vars"
              className="block text-sm font-medium text-foreground"
            >
              {t('settings.whatsappConfig.templateVars')}
            </label>
            <textarea
              id="wa_template_vars"
              value={templateVarsText}
              onChange={(e) => setTemplateVarsText(e.target.value)}
              disabled={!canEditWaba}
              className="input-field mt-1 font-mono text-xs disabled:cursor-not-allowed disabled:opacity-60"
              rows={4}
              placeholder='{"1": "value"}'
            />
            <p className="mt-2 text-xs" style={{ color: 'var(--muted)' }}>
              {t('settings.whatsappConfig.templateVarsHelp')}
            </p>
          </div>
        </div>
      </div>

      {/* Single save -> single toast for the whole config surface */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
        >
          {saving
            ? t('settings.whatsappConfig.saving')
            : t('settings.whatsappConfig.save')}
        </button>
      </div>
    </div>
  );
}

export default WhatsAppConfig;
